import React from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { PixelFactoryContext } from 'pixel-base'
import {
  cancelVendorBill,
  confirmVendorBill,
  deleteVendorBill,
  getVendorBill,
  postVendorBill,
  reverseVendorBill,
} from 'utils/api-routes-accounts'
import FinanceDocumentActions from '../../components/domain/FinanceDocumentActions'
import FinanceDocumentSummary, {
  asStatusNode,
} from '../../components/domain/FinanceDocumentSummary'
import FinanceLineItemsTable from '../../components/domain/FinanceLineItemsTable'
import FinancePageShell from '../../components/FinancePageShell'
import { useFinanceObservable } from '../../hooks/useFinanceObservable'
import { Invoice } from '../../types/finance'
import formatFinanceDate from '../../utils/formatFinanceDate'

const VendorBillDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const pixelFactory = React.useContext(PixelFactoryContext) as any
  const bill = useFinanceObservable<Invoice>(() => getVendorBill(id), {
    immediate: Boolean(id),
  })
  const row = bill.data
  const state = (row?.state || 'draft').toLowerCase()
  const canEdit = ['draft', 'confirmed'].includes(state)
  const canConfirm = state === 'draft'
  const canPost = state === 'confirmed'
  const canReverse = ['posted', 'partially_paid', 'paid'].includes(state)
  const canCancel = ['draft', 'confirmed'].includes(state)
  const canDelete = ['draft', 'confirmed'].includes(state)

  const runAction = (request, successMessage: string, goBack = false) => {
    request.subscribe({
      next: () => {
        pixelFactory?.showSuccess?.(successMessage)
        if (goBack) navigate('/finance/vendor-bills')
        else bill.refresh()
      },
      error: () => pixelFactory?.showFailure?.('Vendor bill action failed'),
    })
  }

  return (
    <FinancePageShell
      title={row?.number || `Vendor Bill #${id}`}
      breadcrumbs={[
        { name: 'Finance', active: false },
        { name: 'Vendor Bills', active: false },
        { name: row?.number || `Vendor Bill #${id}`, active: true },
      ]}
      isLoading={bill.loading}
    >
      {row && (
        <div style={{ display: 'grid', gap: 20 }}>
          <FinanceDocumentSummary
            title="Vendor Bill Summary"
            status={asStatusNode(row.state)}
            fields={[
              {
                label: 'Vendor',
                value: row.partner?.name || row.partner_id || '--',
              },
              {
                label: 'Journal',
                value: row.journal?.name || row.journal_id || '--',
              },
              {
                label: 'Document Date',
                value: formatFinanceDate(row.document_date),
              },
              {
                label: 'Posting Date',
                value: formatFinanceDate(row.posting_date),
              },
              { label: 'Due Date', value: formatFinanceDate(row.due_date) },
              {
                label: 'Total',
                value: row.amount_total ?? '--',
                emphasis: true,
              },
              {
                label: 'Residual',
                value: row.amount_residual ?? '--',
                emphasis: true,
              },
              { label: 'Posted Move', value: row.journal_entry_id || '--' },
            ]}
          />
          <FinanceLineItemsTable variant="vendorBill" rows={row.lines || []} />
          <FinanceDocumentActions
            actions={[
              {
                key: 'back',
                label: 'Back To Edit',
                tone: 'secondary',
                hidden: !canEdit,
                onClick: () => navigate('/finance/vendor-bills'),
              },
              {
                key: 'confirm',
                label: 'Confirm',
                tone: 'primary',
                hidden: !canConfirm,
                onClick: () =>
                  runAction(confirmVendorBill(row.id), 'Vendor bill confirmed'),
              },
              {
                key: 'post',
                label: 'Post',
                tone: 'success',
                hidden: !canPost,
                onClick: () =>
                  runAction(
                    postVendorBill(row.id, {
                      idempotency_key: `bill-post-${row.id}`,
                    }),
                    'Vendor bill posted',
                  ),
              },
              {
                key: 'reverse',
                label: 'Reverse',
                tone: 'warning',
                hidden: !canReverse,
                onClick: () =>
                  runAction(
                    reverseVendorBill(row.id, {
                      idempotency_key: `bill-reverse-${row.id}`,
                    }),
                    'Vendor bill reversed',
                  ),
              },
              {
                key: 'cancel',
                label: 'Cancel',
                tone: 'danger',
                hidden: !canCancel,
                onClick: () =>
                  runAction(cancelVendorBill(row.id), 'Vendor bill cancelled'),
              },
              {
                key: 'delete',
                label: 'Delete',
                tone: 'danger',
                hidden: !canDelete,
                onClick: () =>
                  runAction(
                    deleteVendorBill(row.id),
                    'Vendor bill deleted',
                    true,
                  ),
              },
            ]}
          />
        </div>
      )}
    </FinancePageShell>
  )
}

export default VendorBillDetail
